<template>
  <div class="youth-page">
    <div class="youth-shell">
      <!-- 控制中心：说明 + 主操作 + 唯一倒计时 -->
      <section class="youth-hero">
        <div class="hero-copy">
          <p class="eyebrow">YOUTH PROTECTION</p>
          <h1>青少年模式</h1>
          <p>开启后本账号进入 2 小时保护倒计时，阅读、社区和付费内容会按年龄权限限制，任何设备登录本账号均生效。关闭青少年模式需要完成密码认证。</p>
          <div class="hero-actions">
            <button v-if="!isYouthActive" type="button" class="primary-action" @click="openEnableAuth">开启青少年模式</button>
            <button v-else type="button" class="primary-action" @click="openAdultAuth">关闭青少年模式</button>
            <router-link v-if="notLoggedIn" to="/login?redirect=/youth-protection" class="ghost-action">去登录</router-link>
          </div>
        </div>

        <div class="status-card" :class="{ active: isYouthActive && !store.locked, locked: store.locked }">
          <span class="status-label">{{ statusLabel }}</span>
          <strong class="status-countdown">{{ displayCountdown }}</strong>
          <div class="timer-progress">
            <i :style="{ width: timerPercent + '%' }"></i>
          </div>
          <small class="status-caption">{{ countdownCaption }}</small>
        </div>
      </section>

      <div class="youth-layout">
        <main class="setting-panel">
          <div class="panel-head">
            <div>
              <span class="small-label">权限设置</span>
              <h2>年龄权限与使用规则</h2>
            </div>
          </div>

          <section class="setting-card">
            <h3 class="card-title">监护人密码</h3>
            <div class="password-status">
              <strong>{{ store.hasGuardianCode ? '监护人密码已设置' : '尚未设置监护人密码' }}</strong>
              <small>首次开启时在确认弹窗中设置密码；开启或修改规则时不会重复要求输入两次。</small>
            </div>
            <div v-if="!isYouthActive && store.hasGuardianCode" class="password-change-field">
              <label>
                <span>修改密码（可选）</span>
                <input
                  v-model.trim="form.guardianCode"
                  type="password"
                  name="youth-guardian-new"
                  autocomplete="new-password"
                  maxlength="12"
                  placeholder="输入新密码后点击保存"
                >
                <small>点击保存后会弹窗验证当前密码。</small>
              </label>
            </div>
          </section>

          <section class="setting-card">
            <h3 class="card-title">内容权限</h3>
            <div class="age-grid single-profile">
              <button
                v-for="profile in ageProfiles"
                :key="profile.id"
                type="button"
                :class="{ active: form.ageGroup === profile.id }"
                @click="applyProfile(profile.id)"
              >
                <strong>{{ profile.name }}</strong>
                <span>{{ profile.summary }}</span>
              </button>
            </div>
            <div class="form-grid">
              <label>
                  <span>内容权限等级</span>
                  <select v-model="form.contentLevel">
                    <option value="child">少儿友好</option>
                    <option value="teen">青少年可读</option>
                  </select>
                </label>
                <label>
                  <span>单次可用时长</span>
                  <div class="fixed-value">固定 120 分钟</div>
                </label>
            </div>
          </section>

          <section class="setting-card">
            <h3 class="card-title">使用规则</h3>
            <div class="rule-list">
              <label v-for="rule in rules" :key="rule.id" class="rule-item">
                <input type="checkbox" v-model="form.rules" :value="rule.id">
                <span>
                  <strong>{{ rule.title }}</strong>
                  <small>{{ rule.desc }}</small>
                </span>
              </label>
            </div>
          </section>

          <div class="action-row">
            <button type="button" class="primary-action" @click="saveSettings">保存权限设置</button>
            <button type="button" class="ghost-action" @click="resetSettings">恢复默认</button>
          </div>
          <p class="save-tip">{{ saveMessage }}</p>
        </main>

        <aside class="preview-panel">
          <section class="side-card">
            <h2>访问示例</h2>
            <p v-if="!isYouthActive && !store.locked" class="preview-note">当前模式未开启，下方限制将在开启后生效。</p>
            <div class="access-list">
              <div v-for="item in accessPreview" :key="item.name" :class="{ blocked: item.blocked }">
                <span>{{ item.name }}</span>
                <strong>{{ item.blocked ? '限制' : '允许' }}</strong>
              </div>
            </div>
          </section>

        </aside>
      </div>
    </div>

    <div class="auth-modal" v-if="showAdultAuth" @click.self="closeAdultAuth">
      <div class="auth-panel">
        <button type="button" class="auth-close" @click="closeAdultAuth">×</button>
        <span class="small-label">密码认证</span>
        <h2>{{ authModalTitle }}</h2>
        <p>{{ authModalHint }}</p>
        <input
          v-model.trim="authPassword"
          type="password"
          name="youth-auth-code"
          autocomplete="new-password"
          maxlength="12"
          :placeholder="authModalPlaceholder"
          @keyup.enter="confirmAdultAuth"
        >
        <div class="auth-actions">
          <button type="button" class="primary-action" @click="confirmAdultAuth">{{ authModalConfirmText }}</button>
          <button type="button" class="ghost-action" @click="closeAdultAuth">取消</button>
        </div>
        <p class="auth-message" :class="{ 'auth-error': adultAuthError }">{{ adultAuthMessage }}</p>
      </div>
    </div>

    <YouthNoticeDialog
      :visible="noticeVisible"
      :title="noticeTitle"
      :description="noticeDesc"
      :type="noticeType"
      @close="noticeVisible = false"
    />
  </div>
</template>

<script>
import { useRouter } from 'vue-router'
import YouthNoticeDialog from './YouthNoticeDialog.vue'
import {
  youthStore,
  initYouthStore,
  refreshYouthState,
  saveYouthSettings,
  enableYouthMode,
  authYouthMode,
  resetYouthSettings
} from '../utils/youthContent'

const sessionMinutes = 120
const sessionMs = sessionMinutes * 60 * 1000

export default {
  name: 'YouthProtectionPage',
  components: {
    YouthNoticeDialog
  },
  setup() {
    const router = useRouter()
    return { router }
  },
  data() {
    return {
      loading: true,
      noticeVisible: false,
      noticeTitle: '',
      noticeDesc: '',
      noticeType: 'success',
      form: {
        ageGroup: 'under18',
        guardianCode: '',
        dailyLimit: sessionMinutes,
        contentLevel: 'teen',
        rules: ['nightLock', 'paidConfirm', 'communityLimit']
      },
      showAdultAuth: false,
      authMode: 'disable', // disable=关闭认证 / enable=开启认证 / setPassword=首次设置密码并开启 / saveSettings=验证后保存 / resetSettings=验证后恢复默认
      authPassword: '',
      adultAuthMessage: '请输入密码完成认证。',
      adultAuthError: false,
      pendingSavePayload: null,
      pendingCodeChange: false, // 保存时是否正在修改监护人密码（需要验证当前密码）
      saveMessage: '请先设置密码，开启后本账号进入 2 小时倒计时；到期后整个网站会锁定。',
      ageProfiles: [
        { id: 'under18', name: '18 岁以下', summary: '开启后按下方勾选规则限制内容、社区互动和付费操作。', limit: sessionMinutes, level: 'teen' }
      ],
      rules: [
        { id: 'nightLock', title: '夜间禁用', desc: '22:00 到次日 06:00 限制阅读。' },
        { id: 'paidConfirm', title: '付费确认', desc: '青少年模式下限制付费章节阅读与充值。' },
        { id: 'communityLimit', title: '社区限制', desc: '社区发帖和评论需要监护授权。' }
      ]
    }
  },
  computed: {
    store() {
      return youthStore
    },
    notLoggedIn() {
      return !youthStore.loggedIn
    },
    isYouthActive() {
      return youthStore.enabled
    },
    countdownLeft() {
      return youthStore.remaining
    },
    countdownText() {
      const total = Math.max(0, this.countdownLeft)
      const hours = Math.floor(total / 3600000)
      const minutes = Math.floor((total % 3600000) / 60000)
      const seconds = Math.floor((total % 60000) / 1000)
      return [hours, minutes, seconds].map(item => String(item).padStart(2, '0')).join(':')
    },
    displayCountdown() {
      if (this.store.locked) return '00:00:00'
      if (!this.isYouthActive) return '02:00:00'
      return this.countdownText
    },
    countdownCaption() {
      if (this.store.locked) return '本次倒计时已结束，整个网站将保持锁定。'
      if (!this.isYouthActive) return '开启青少年模式后自动开始 2 小时倒计时。'
      return '本次青少年模式剩余可用时间。'
    },
    timerPercent() {
      if (!this.isYouthActive || this.store.locked) return this.isYouthActive ? 0 : 100
      return Math.max(0, Math.round((this.countdownLeft / sessionMs) * 100))
    },
    statusLabel() {
      if (this.store.locked) return '已锁定'
      if (this.isYouthActive) return '保护中'
      return '未开启'
    },
    canEditGuardianCode() {
      return !youthStore.enabled || !youthStore.hasGuardianCode
    },
    authModalTitle() {
      if (this.authMode === 'disable') return '关闭青少年模式'
      if (this.authMode === 'setPassword') return '设置密码并开启'
      if (this.authMode === 'saveSettings') return '验证密码'
      if (this.authMode === 'resetSettings') return '验证密码'
      return '开启青少年模式'
    },
    authModalHint() {
      if (this.authMode === 'disable') return '请输入密码。认证通过后会退出青少年模式，并解除本账号当前的年龄权限限制。'
      if (this.authMode === 'setPassword') return '首次开启需要设置监护人密码（4-12 位）。开启后进入 2 小时倒计时，关闭模式时需要输入该密码。'
      if (this.authMode === 'saveSettings') {
        return this.pendingCodeChange
          ? '修改监护人密码需要先验证当前密码。'
          : '青少年模式开启期间，修改使用规则等设置需要先验证密码。'
      }
      if (this.authMode === 'resetSettings') return '恢复默认设置需要先验证监护人密码。'
      return '请输入密码确认开启。开启后本账号进入 2 小时保护倒计时。'
    },
    authModalPlaceholder() {
      return this.authMode === 'setPassword' ? '请设置至少 4 位密码' : '请输入密码'
    },
    authModalConfirmText() {
      if (this.authMode === 'disable') return '确认关闭'
      if (this.authMode === 'saveSettings') return '确认保存'
      if (this.authMode === 'resetSettings') return '确认恢复'
      return '确认开启'
    },
    accessPreview() {
      const active = this.isYouthActive
      const expired = this.store.locked
      return [
        { name: '全站访问', blocked: expired },
        { name: '免费漫画阅读', blocked: expired },
        { name: '付费章节阅读', blocked: (active || expired) && this.form.rules.includes('paidConfirm') },
        { name: '社区发帖评论', blocked: (active || expired) && this.form.rules.includes('communityLimit') },
        { name: '充值/开通会员', blocked: (active || expired) && this.form.rules.includes('paidConfirm') },
        { name: '关闭青少年模式', blocked: active || expired },
        { name: '夜间阅读', blocked: (active || expired) && this.form.rules.includes('nightLock') }
      ]
    }
  },
  mounted() {
    this.loadInitial()
  },
  watch: {
    showAdultAuth(visible) {
      if (visible) {
        this.adultAuthError = false
      }
    }
  },
  methods: {
    async loadInitial() {
      initYouthStore()
      await refreshYouthState()
      if (youthStore.loggedIn) {
        this.syncFormFromStore()
      }
      this.loading = false
    },
    syncFormFromStore() {
      this.form.contentLevel = youthStore.contentLevel === 'child' ? 'child' : 'teen'
      this.form.ageGroup = youthStore.ageGroup || 'under18'
      this.form.dailyLimit = youthStore.dailyLimit || sessionMinutes
      this.form.rules = Array.isArray(youthStore.rules)
        ? youthStore.rules.filter(item => this.rules.some(rule => rule.id === item))
        : []
    },
    applyProfile(id) {
      const profile = this.ageProfiles.find(item => item.id === id)
      if (!profile) return
      this.form.ageGroup = profile.id
      this.form.dailyLimit = profile.limit
      this.form.contentLevel = profile.level
    },
    openEnableAuth() {
      if (this.notLoggedIn) {
        this.saveMessage = '青少年模式与登录账号绑定，请先登录后再开启。'
        this.router.push('/login?redirect=/youth-protection')
        return
      }
      if (this.isYouthActive && !this.store.locked) {
        refreshYouthState()
        this.saveMessage = '青少年模式已经打开，当前倒计时将继续，不会重置。'
        return
      }
      if (this.store.locked) {
        this.saveMessage = '青少年模式已经开启，但本次使用时间已到，网站将保持锁定。'
        return
      }
      this.authMode = youthStore.hasGuardianCode ? 'enable' : 'setPassword'
      this.showAdultAuth = true
      this.authPassword = ''
      this.adultAuthMessage = youthStore.hasGuardianCode ? '请输入密码确认开启。' : '请设置至少 4 位密码。'
    },
    async saveSettings() {
      if (this.notLoggedIn) {
        this.saveMessage = '请先登录后再保存青少年模式设置。'
        return
      }
      const payload = {
        contentLevel: this.form.contentLevel,
        rules: this.form.rules
      }
      if (this.canEditGuardianCode && this.form.guardianCode) {
        payload.guardianCode = this.form.guardianCode
      }
      // 已有密码时修改密码：必须先验证当前密码（无论模式是否开启）
      const changingCode = this.canEditGuardianCode && !!this.form.guardianCode && youthStore.hasGuardianCode
      if (changingCode) {
        this.pendingCodeChange = true
        this.pendingSavePayload = payload
        this.authMode = 'saveSettings'
        this.showAdultAuth = true
        this.authPassword = ''
        this.adultAuthMessage = '修改监护人密码需要先验证当前密码。'
        return
      }
      if (youthStore.enabled) {
        // 青少年模式开启期间，修改设置（含使用规则）必须先验证密码
        this.pendingCodeChange = false
        this.pendingSavePayload = payload
        this.authMode = 'saveSettings'
        this.showAdultAuth = true
        this.authPassword = ''
        this.adultAuthMessage = '青少年模式开启期间，修改设置需要先验证密码。'
        return
      }
      await this.performSave(payload)
    },
    async performSave(payload) {
      try {
        const res = await saveYouthSettings(payload)
        this.form.guardianCode = ''
        this.syncFormFromStore()
        this.saveMessage = (res && res.msg) || '已保存青少年模式设置。开启后将固定倒计时 2 小时。'
      } catch (error) {
        this.saveMessage = (error && error.message) || '保存失败，请重试。'
      }
    },
    async resetSettings() {
      if (this.notLoggedIn) {
        this.saveMessage = '请先登录后再恢复默认设置。'
        return
      }
      if (youthStore.hasGuardianCode) {
        // 只要已有监护人密码，恢复默认设置都必须先验证
        this.authMode = 'resetSettings'
        this.showAdultAuth = true
        this.authPassword = ''
        this.adultAuthMessage = '恢复默认设置需要先验证监护人密码。'
        return
      }
      await this.performReset('')
    },
    async performReset(guardianCode) {
      try {
        const res = await resetYouthSettings(guardianCode)
        this.resetLocalForm()
        this.saveMessage = (res && res.msg) || '已恢复默认青少年模式配置。'
      } catch (error) {
        this.saveMessage = (error && error.message) || '恢复默认失败，请重试。'
      }
    },
    resetLocalForm() {
      this.form = {
        ageGroup: 'under18',
        guardianCode: '',
        dailyLimit: sessionMinutes,
        contentLevel: 'teen',
        rules: ['nightLock', 'paidConfirm', 'communityLimit']
      }
    },
    openAdultAuth() {
      if (!youthStore.enabled) {
        this.saveMessage = '青少年模式未开启。'
        return
      }
      if (!youthStore.hasGuardianCode) {
        this.showAdultAuth = false
        this.saveMessage = '请先完成密码设置，才能关闭青少年模式。'
        return
      }
      this.authMode = 'disable'
      this.showAdultAuth = true
      this.authPassword = ''
      this.adultAuthMessage = '请输入密码完成认证。'
    },
    closeAdultAuth() {
      this.showAdultAuth = false
      this.pendingCodeChange = false
    },
    showNotice(title, description, type) {
      this.noticeTitle = title
      this.noticeDesc = description
      this.noticeType = type || 'success'
      this.noticeVisible = true
    },
    async confirmAdultAuth() {
      const password = this.authPassword
      if (!password) {
        this.adultAuthMessage = '请输入密码。'
        return
      }
      if (this.authMode === 'setPassword' && (password.length < 4 || password.length > 12)) {
        this.adultAuthMessage = '密码长度需为4-12位。'
        return
      }
      this.adultAuthMessage = '正在验证...'
      this.adultAuthError = false
      const mode = this.authMode
      try {
        let res = null
        if (mode === 'disable') {
          res = await authYouthMode(password)
        } else if (mode === 'saveSettings') {
          res = await saveYouthSettings({ ...this.pendingSavePayload, currentCode: password })
          this.syncFormFromStore()
          this.pendingSavePayload = null
          this.pendingCodeChange = false
          this.form.guardianCode = ''
        } else if (mode === 'resetSettings') {
          res = await resetYouthSettings(password)
          this.resetLocalForm()
        } else {
          res = await enableYouthMode(password)
        }
        this.showAdultAuth = false
        this.authPassword = ''
        let message = res && res.msg
        if (!message) {
          if (mode === 'disable') message = '密码认证通过，青少年模式已关闭。'
          else if (mode === 'saveSettings') message = '已保存青少年模式设置。'
          else if (mode === 'resetSettings') message = '已恢复默认青少年模式配置。'
          else message = '青少年模式已开启，2 小时倒计时已开始。'
        }
        this.saveMessage = message

        // 开启 / 关闭成功后弹出醒目提示
        if (mode === 'disable') {
          this.showNotice('青少年模式已关闭', '密码认证通过，本账号已退出青少年模式，年龄权限限制已解除。', 'info')
        } else if (mode === 'enable' || mode === 'setPassword') {
          this.showNotice('青少年模式已开启', '本账号已进入 2 小时保护倒计时，到期后整个网站将锁定；关闭模式需要输入密码。', 'success')
        }
      } catch (error) {
        const message = (error && error.message) || '验证失败，请重试。'
        this.adultAuthMessage = message
        this.adultAuthError = true
        // 双保险：弹窗内红字 + 顶部全局 Toast
        this.$message.error(message)
      }
    }
  }
}
</script>

<style scoped>
.youth-page {
  min-height: 100vh;
  padding: 34px 0 64px;
  color: #eef3ff;
  background:
    radial-gradient(circle at 14% 0%, rgba(255, 204, 102, 0.11), transparent 32%),
    radial-gradient(circle at 86% 14%, rgba(35, 211, 238, 0.12), transparent 28%),
    linear-gradient(180deg, rgba(7, 9, 18, 0.2), rgba(7, 9, 18, 0.72));
}

.youth-shell {
  width: min(1500px, 100%);
  margin: 0 auto;
  padding: 0 32px;
}

/* ============ 顶部控制中心 ============ */

.youth-hero,
.setting-panel,
.side-card,
.setting-card,
.status-card,
.auth-panel {
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(180deg, rgba(20, 24, 39, 0.92), rgba(12, 15, 27, 0.94));
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.32);
}

.youth-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 28px;
  align-items: stretch;
  padding: 28px;
}

.hero-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.eyebrow,
.small-label {
  color: #23d3ee;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0;
}

.youth-hero h1 {
  margin: 8px 0 10px;
  color: #f5f7ff;
  font-size: clamp(30px, 3vw, 44px);
  line-height: 1.08;
}

.youth-hero p,
.save-tip,
.age-grid span,
.rule-item small,
.auth-panel p,
.auth-message {
  color: #9faaca;
  line-height: 1.72;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 18px;
}

/* 状态卡：页面内唯一的倒计时展示位 */
.status-card {
  display: grid;
  align-content: center;
  gap: 10px;
  padding: 20px;
  border-color: rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.05);
}

.status-card.active {
  border-color: rgba(255, 204, 102, 0.35);
  background: linear-gradient(135deg, rgba(255, 204, 102, 0.16), rgba(35, 211, 238, 0.14));
}

.status-card.locked {
  border-color: rgba(255, 122, 107, 0.4);
  background: linear-gradient(135deg, rgba(255, 122, 107, 0.16), rgba(255, 204, 102, 0.08));
}

.status-label {
  color: #23d3ee;
  font-size: 13px;
  font-weight: 900;
}

.status-card.locked .status-label {
  color: #ff9c8f;
}

.status-countdown {
  color: #ffcc66;
  font-size: 46px;
  line-height: 1;
  letter-spacing: 0.5px;
  font-variant-numeric: tabular-nums;
}

.status-caption {
  color: #9faaca;
  font-size: 13px;
  line-height: 1.6;
}

.timer-progress {
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
}

.timer-progress i {
  display: block;
  height: 100%;
  min-width: 0;
  border-radius: inherit;
  background: linear-gradient(135deg, #ffcc66, #23d3ee);
  transition: width 0.35s ease;
}

.status-card.locked .timer-progress i {
  background: linear-gradient(135deg, #ff9c8f, #ffcc66);
}

/* ============ 主体布局 ============ */

.youth-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 22px;
  align-items: start;
  margin-top: 22px;
}

.setting-panel,
.side-card {
  padding: 22px;
}

.panel-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  margin-bottom: 18px;
}

.panel-head h2,
.side-card h2 {
  margin: 6px 0 0;
  color: #f5f7ff;
}

/* 设置卡片分组 */
.setting-card {
  margin-top: 16px;
  padding: 18px 20px 20px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  box-shadow: none;
}

.setting-card:first-of-type {
  margin-top: 0;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  color: #f5f7ff;
  font-size: 15px;
  font-weight: 900;
}

.card-title::before {
  content: "";
  width: 4px;
  height: 16px;
  border-radius: 999px;
  background: linear-gradient(180deg, #ffcc66, #23d3ee);
}

/* 年龄/表单/规则 */
.age-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.age-grid button,
.rule-item,
.summary-row,
.access-list div {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
}

.age-grid button {
  display: grid;
  gap: 8px;
  min-height: 96px;
  padding: 16px;
  color: #dfe7ff;
  text-align: left;
  cursor: pointer;
}

.age-grid button.active {
  color: #07101d;
  background: linear-gradient(135deg, #ffcc66, #23d3ee);
}

.age-grid button.active span {
  color: rgba(7, 16, 29, 0.78);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.form-grid label {
  display: grid;
  gap: 8px;
  color: #f5f7ff;
  font-weight: 800;
}

.form-grid input,
.form-grid select,
.auth-panel input {
  width: 100%;
  min-height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 0 13px;
  color: #eef3ff;
  outline: none;
  background: rgba(7, 10, 18, 0.82);
}

.form-grid input:focus,
.form-grid select:focus,
.auth-panel input:focus {
  border-color: #23d3ee;
  box-shadow: 0 0 0 4px rgba(35, 211, 238, 0.12);
}

.form-grid input:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.password-status,
.password-change-field {
  display: grid;
  gap: 8px;
}

.password-status {
  padding: 14px;
  border: 1px solid rgba(35, 211, 238, 0.18);
  border-radius: 8px;
  background: rgba(35, 211, 238, 0.06);
}

.password-status small,
.password-change-field small,
.preview-note {
  color: #9faaca;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
}

.password-change-field {
  margin-top: 14px;
}

.password-change-field label {
  display: grid;
  gap: 8px;
  color: #f5f7ff;
  font-weight: 800;
}

.password-change-field input,
.fixed-value {
  width: 100%;
  min-height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 0 12px;
  color: #eef3ff;
  background: rgba(7, 10, 18, 0.82);
  display: flex;
  align-items: center;
}

.fixed-value {
  color: #cbd5ef;
}

.password-lock-hint {
  color: #ffcc66;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
}

.rule-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.rule-item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px;
  cursor: pointer;
}

.rule-item input {
  margin-top: 3px;
  width: 18px;
  height: 18px;
}

.rule-item span {
  display: grid;
  gap: 5px;
}

.rule-item strong {
  color: #f5f7ff;
}

/* 操作行 */
.action-row,
.auth-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 18px;
}

.primary-action,
.ghost-action {
  min-height: 42px;
  border: 0;
  border-radius: 8px;
  padding: 0 16px;
  font-weight: 900;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.primary-action {
  color: #07101d;
  background: linear-gradient(135deg, #ffcc66, #23d3ee);
}

.ghost-action {
  color: #dfe7ff;
  background: rgba(255, 255, 255, 0.08);
}

.save-tip {
  margin-top: 12px;
  font-size: 13px;
}

/* ============ 右侧预览 ============ */

.preview-panel {
  display: grid;
  gap: 16px;
  position: sticky;
  top: 18px;
}

.preview-note {
  margin: 8px 0 0;
}

.summary-row,
.access-list div {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  min-height: 44px;
  margin-top: 10px;
  padding: 0 12px;
}

.summary-row span,
.access-list span {
  color: #9faaca;
}

.summary-row strong,
.access-list strong {
  color: #f5f7ff;
}

.access-list .blocked strong {
  color: #ffcc66;
}

/* ============ 认证弹窗 ============ */

.auth-modal {
  position: fixed;
  inset: 0;
  z-index: 960;
  display: grid;
  place-items: center;
  padding: 18px;
  background: rgba(0, 0, 0, 0.68);
}

.auth-panel {
  position: relative;
  width: min(420px, 100%);
  padding: 24px;
}

.auth-close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 8px;
  color: #dfe7ff;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.08);
}

.auth-panel h2 {
  margin: 8px 0 10px;
  color: #f5f7ff;
}

.auth-panel input {
  margin-top: 16px;
}

.auth-message {
  margin-top: 10px;
  font-size: 13px;
}

.auth-message.auth-error {
  color: #ff8a80;
  font-weight: 700;
}

/* ============ 响应式 ============ */

@media (max-width: 1180px) {
  .youth-layout {
    grid-template-columns: 1fr;
  }

  .preview-panel {
    position: static;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 920px) {
  .youth-hero {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .youth-shell {
    padding: 0 16px;
  }

  .youth-hero,
  .panel-head {
    display: grid;
  }

  .form-grid,
  .rule-list,
  .preview-panel {
    grid-template-columns: 1fr;
  }

  .age-grid {
    grid-template-columns: 1fr;
  }

  .status-countdown {
    font-size: 38px;
  }
}
</style>
